import type { IntentConfig, ResolvedGuardrails } from "@fathom/intent";
import type { ProjectionContext } from "../types.js";

/**
 * Generate a Claude Code SKILL.md file from intent and guardrails.
 *
 * This is the primary projection — it should be thorough, well-structured,
 * and read naturally as instructions for an AI coding assistant.
 */
export function generateClaudeSkill(
  intent: IntentConfig,
  guardrails: ResolvedGuardrails,
  context?: ProjectionContext,
): string {
  const sections: string[] = [];

  // Header
  sections.push(`# ${intent.project}`);

  // ── Project Context ────────────────────────────────────────
  sections.push(buildProjectContext(intent));

  // ── What Matters ───────────────────────────────────────────
  if (intent.values.length > 0) {
    sections.push(buildValues(intent));
  }

  // ── Budget Awareness ───────────────────────────────────────
  const budgetSection = buildBudget(intent, context);
  if (budgetSection) {
    sections.push(budgetSection);
  }

  // ── Guardrails ─────────────────────────────────────────────
  const guardrailSection = buildGuardrails(guardrails);
  if (guardrailSection) {
    sections.push(guardrailSection);
  }

  // ── Tech Context ───────────────────────────────────────────
  const techSection = buildTech(intent);
  if (techSection) {
    sections.push(techSection);
  }

  // ── Feature Registry ───────────────────────────────────────
  const registrySection = buildRegistry(context);
  if (registrySection) {
    sections.push(registrySection);
  }

  return sections.join("\n\n") + "\n";
}

function buildProjectContext(intent: IntentConfig): string {
  const lines: string[] = ["## Project Context"];
  lines.push("");
  lines.push(intent.description);
  if (intent.audience) {
    lines.push("");
    lines.push(`**Audience:** ${intent.audience}`);
  }
  return lines.join("\n");
}

function buildValues(intent: IntentConfig): string {
  const lines: string[] = ["## What Matters"];
  lines.push("");
  lines.push(
    "These are the project's core values, ordered by priority. Let them guide your decisions.",
  );
  lines.push("");

  // Sort by priority weight
  const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
  const sorted = [...intent.values].sort(
    (a, b) => priorityOrder[a.priority] - priorityOrder[b.priority],
  );

  for (const value of sorted) {
    const tag =
      value.priority === "critical"
        ? " **(CRITICAL)**"
        : value.priority === "high"
          ? " **(high priority)**"
          : "";
    lines.push(`- **${value.name}**${tag} — ${value.description}`);
    if (value.details) {
      lines.push(`  - ${value.details}`);
    }
  }

  return lines.join("\n");
}

function buildBudget(
  intent: IntentConfig,
  context?: ProjectionContext,
): string | null {
  if (!intent.budget) return null;

  const { budget } = intent;
  const lines: string[] = ["## Budget Awareness"];
  lines.push("");
  lines.push(
    `This project has a monthly budget of **${budget.currency} ${budget.monthly_limit.toLocaleString()}**.`,
  );
  lines.push(
    `Alert threshold: ${Math.round(budget.alert_threshold * 100)}% of budget.`,
  );

  if (budget.prefer_batch) {
    lines.push("");
    lines.push(
      "**Prefer batch operations** where possible to reduce API costs.",
    );
  }

  if (context?.trackingSummary) {
    const { totalCost, monthlySpend, totalTokens } = context.trackingSummary;
    const pct =
      budget.monthly_limit > 0
        ? Math.round((monthlySpend / budget.monthly_limit) * 100)
        : 0;
    lines.push("");
    lines.push("### Current Spend");
    lines.push(`- Monthly spend so far: ${budget.currency} ${monthlySpend.toFixed(2)} (${pct}% of budget)`);
    lines.push(`- Total project cost: ${budget.currency} ${totalCost.toFixed(2)}`);
    lines.push(`- Total tokens used: ${totalTokens.toLocaleString()}`);

    if (pct >= budget.alert_threshold * 100) {
      lines.push("");
      lines.push(
        `> **Warning:** Monthly spend has reached ${pct}% of the budget. Be especially mindful of token usage.`,
      );
    }
  }

  return lines.join("\n");
}

function buildGuardrails(guardrails: ResolvedGuardrails): string | null {
  const hasAny =
    guardrails.security.length > 0 ||
    guardrails.quality.length > 0 ||
    guardrails.process.length > 0;

  if (!hasAny) return null;

  const lines: string[] = ["## Guardrails"];
  lines.push("");
  lines.push("These rules are non-negotiable. Follow them in every change.");

  if (guardrails.security.length > 0) {
    lines.push("");
    lines.push("### Security");
    for (const rule of guardrails.security) {
      lines.push(`- ${rule}`);
    }
  }

  if (guardrails.quality.length > 0) {
    lines.push("");
    lines.push("### Quality");
    for (const rule of guardrails.quality) {
      lines.push(`- ${rule}`);
    }
  }

  if (guardrails.process.length > 0) {
    lines.push("");
    lines.push("### Process");
    for (const rule of guardrails.process) {
      lines.push(`- ${rule}`);
    }
  }

  return lines.join("\n");
}

function buildTech(intent: IntentConfig): string | null {
  if (!intent.tech) return null;
  const { stack, opinions } = intent.tech;
  if (stack.length === 0 && opinions.length === 0) return null;

  const lines: string[] = ["## Tech Context"];

  if (stack.length > 0) {
    lines.push("");
    lines.push(`**Stack:** ${stack.join(", ")}`);
  }

  if (opinions.length > 0) {
    lines.push("");
    lines.push("### Conventions");
    for (const opinion of opinions) {
      lines.push(`- ${opinion}`);
    }
  }

  return lines.join("\n");
}

function buildRegistry(context?: ProjectionContext): string | null {
  if (!context?.registry || context.registry.length === 0) return null;

  const lines: string[] = ["## Feature Registry"];
  lines.push("");
  lines.push("Current features being tracked:");
  lines.push("");

  const statusIcon: Record<string, string> = {
    active: "🟢",
    planned: "🔵",
    completed: "✅",
    paused: "⏸️",
  };

  for (const feature of context.registry) {
    const icon = statusIcon[feature.status] ?? "⚪";
    const tokens = feature.estimatedTokens
      ? ` (~${Math.round(feature.estimatedTokens / 1000)}K tokens)`
      : "";
    lines.push(`- ${icon} **${feature.name}** [${feature.status}]${tokens}`);
  }

  return lines.join("\n");
}
